function browserWindow() {
	return typeof window !== "undefined" ? window : {};
}

function browserDocument() {
	return typeof document !== "undefined" ? document : {};
}

function abandonedCartConfig() {
	return browserWindow().fluxVars?.abandonedCart || {};
}

function consentConfig() {
	return browserWindow().fluxVars?.cookieConsent || {};
}

function getCookieValue(cookieString, name) {
	const prefix = `${name}=`;
	return (
		String(cookieString || "")
			.split(";")
			.map((cookie) => cookie.trim())
			.find((cookie) => cookie.startsWith(prefix))
			?.slice(prefix.length) || ""
	);
}

function readStoredConsent() {
	const name = consentConfig().cookieName || "flux_privacy_consent";
	const raw = getCookieValue(browserDocument().cookie || "", name);
	if (!raw) {
		return null;
	}

	try {
		return JSON.parse(decodeURIComponent(raw));
	} catch (err) {
		return null;
	}
}

function currentConsent() {
	const win = browserWindow();
	if (win.fluxCookieConsent && typeof win.fluxCookieConsent.get === "function") {
		return win.fluxCookieConsent.get();
	}

	return readStoredConsent();
}

function consentAllows(category) {
	if (category === "necessary") {
		return true;
	}

	const win = browserWindow();
	if (win.fluxCookieConsent && typeof win.fluxCookieConsent.has === "function") {
		return win.fluxCookieConsent.has(category);
	}

	const consent = readStoredConsent();
	const expectedVersion = consentConfig().version || "";
	if (!consent || (expectedVersion && consent.version !== expectedVersion)) {
		return false;
	}

	return Boolean(consent.categories?.[category]);
}

function text(value) {
	return String(value || "").trim();
}

function fieldValue(selectors) {
	const doc = browserDocument();
	for (const selector of selectors) {
		const field = doc.querySelector?.(selector);
		if (field && typeof field.value === "string" && text(field.value)) {
			return text(field.value);
		}
	}

	return "";
}

function contactPayload() {
	return {
		email: fieldValue([
			".wc-block-components-address-form input[type='email']",
			".wc-block-components-text-input input[type='email']",
			"input#email",
			"input#billing_email",
			"input[name='email']",
			"input[name='billing_email']",
			"input[type='email']",
		]),
		firstName: fieldValue([
			"input#billing-first_name",
			"input#billing_first_name",
			"input[name='billing-first_name']",
			"input[name='billing_first_name']",
			"input[autocomplete='given-name']",
		]),
		lastName: fieldValue([
			"input#billing-last_name",
			"input#billing_last_name",
			"input[name='billing-last_name']",
			"input[name='billing_last_name']",
			"input[autocomplete='family-name']",
		]),
		phone: fieldValue(["input#billing-phone", "input#billing_phone", "input[name='billing_phone']", "input[type='tel']"]),
		consent: currentConsent(),
	};
}

function validEmail(value) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text(value));
}

function checkoutSurfacePresent() {
	const doc = browserDocument();
	return Boolean(
		doc.querySelector?.(".wp-block-woocommerce-checkout") ||
			doc.querySelector?.(".wc-block-checkout") ||
			doc.querySelector?.("form.checkout")
	);
}

function inputTargets() {
	return Array.from(
		browserDocument().querySelectorAll?.(
			[
				".wp-block-woocommerce-checkout input[type='email']",
				".wp-block-woocommerce-checkout input[type='text']",
				".wp-block-woocommerce-checkout input[type='tel']",
				"form.checkout input#billing_email",
				"form.checkout input#billing_first_name",
				"form.checkout input#billing_last_name",
				"form.checkout input#billing_phone",
			].join(",")
		) || []
	);
}

export function abandonedCartCheckoutEvents() {
	const config = abandonedCartConfig();
	if (!config.enabled || !config.endpoint || !config.nonce || !checkoutSurfacePresent()) {
		return;
	}

	const category = config.consentCategory || "necessary";
	let timer = null;
	let lastSentKey = "";

	const send = () => {
		if (!consentAllows(category)) {
			return;
		}

		const payload = contactPayload();
		if (!validEmail(payload.email)) {
			return;
		}

		const key = JSON.stringify({
			email: payload.email,
			firstName: payload.firstName,
			lastName: payload.lastName,
			phone: payload.phone,
		});
		if (key === lastSentKey) {
			return;
		}
		lastSentKey = key;

		fetch(config.endpoint, {
			method: "POST",
			credentials: "same-origin",
			keepalive: true,
			headers: {
				"Content-Type": "application/json",
				"X-WP-Nonce": config.nonce,
			},
			body: JSON.stringify(payload),
		}).catch(() => {});
	};

	const queue = () => {
		if (timer) {
			browserWindow().clearTimeout(timer);
		}
		timer = browserWindow().setTimeout(send, 800);
	};

	const bind = () => {
		inputTargets().forEach((field) => {
			if (field.dataset.fluxAbandonedCartBound === "1") {
				return;
			}
			field.dataset.fluxAbandonedCartBound = "1";
			field.addEventListener("input", queue);
			field.addEventListener("change", queue);
		});
	};

	bind();
	queue();

	const observer = new MutationObserver(() => {
		bind();
	});
	observer.observe(browserDocument().body, { childList: true, subtree: true });

	browserWindow().addEventListener("flux:cookie-consent-updated", queue);
}

